import React, { useState } from 'react';
import { useEditor } from '../context/EditorProvider.jsx';
import { postRequest } from '../../Fetch/request';
import './Explorer.css';

const INDENT=14;
const Arrow = ({collapsed})=> collapsed?'▸':'▾';

export default function Explorer() {
  const { state, dispatch } = useEditor();
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);

  const addFile = () => {
    const name = prompt('Enter filename (e.g., MyClass.java)');
    if (!name) return;
    const base = name.endsWith('.java') ? name : name + '.java';
    const filename = state.selectedFolder ? `${state.selectedFolder}/${base}` : base;
    dispatch({ type: 'ADD_FILE', filename });
  };
  const addFolder = () => {
    const name = prompt('Enter folder name');
    if (!name) return;
    const folderPath = state.selectedFolder ? `${state.selectedFolder}/${name}` : name;
    if (state.folders.some((f) => f === folderPath)) {
      alert('Folder already exists at this location');
      return;
    }
    dispatch({ type: 'ADD_FOLDER', folder: folderPath });
  };

  const runFiles = async () => {
    dispatch({ type: 'SET_LOADING', loading: true });
    dispatch({ type: 'SET_OUTPUT', output: '' });
    try {
      // Grab userId (email) from localStorage profile
      const profile = JSON.parse(localStorage.getItem('profileObject') || '{}');
      const userId = profile.id || localStorage.getItem('userId') || '';

      // Try to read problemId either from query string or from URL pattern "/{id}/editor"
      const params = new URLSearchParams(window.location.search);
      let problemId = params.get('problemId') || params.get('lldProblemId');
      if(!problemId){
        const match = window.location.pathname.match(/\/([^/]+)\/editor$/);
        if(match) problemId = match[1];
      }

      const payload = { 
        files: state.files, 
        userId, 
        problemId,
        folders: state.folders // Added folders to save in one API call
      };

      // Make single API call that both executes and saves the code
      const postResponse = await postRequest(`${process.env.REACT_APP_URL}/api/lambda/java/execute`, payload);
      
      // Process the response
      if (postResponse && postResponse.status === "200") {
        const resObj = postResponse.response || {};
        // Check for logs in the correct path: response.result.logs
        const out = resObj?.result?.logs || resObj?.logs || [];

        // Convert array to formatted string with line breaks
        const formattedOutput = Array.isArray(out) ? out.join('\n') : out;
        dispatch({ type: 'SET_OUTPUT', output: formattedOutput || 'No output' });
        
        // After successful run, immediately clear unsaved changes flag
        dispatch({ type: 'CLEAR_UNSAVED_CHANGES' });
        
        // Set the last run time to indicate code was saved
        if (problemId) {
          localStorage.setItem(`lld-last-run-${problemId}`, Date.now().toString());
        }
      } else {
        console.error("Error in Java run request:", postResponse);
        dispatch({ type: 'SET_OUTPUT', output: 'Error: Request failed' });
      }
    } catch (err) {
      console.error("Error running Java files:", err);
      dispatch({ type: 'SET_OUTPUT', output: 'Error: ' + err.message });
    } finally {
      dispatch({ type: 'SET_LOADING', loading: false });
    }
  };

  const deleteFile = (id) => {
    if(!window.confirm('Delete file?')) return;
    dispatch({ type: 'DELETE_FILE', id });
  };

  const deleteFolder = (folderPath) => {
    if(!window.confirm('Delete folder and its contents?')) return;
    dispatch({ type: 'DELETE_FOLDER', folder: folderPath });
  };

  const renameFile = (id, currentFilename) => {
    const newName = prompt('Enter new filename:', currentFilename.split('/').pop());
    if (!newName || newName === currentFilename.split('/').pop()) return;
    
    const pathParts = currentFilename.split('/');
    pathParts.pop(); // Remove the filename
    const newFilename = pathParts.length > 0 ? `${pathParts.join('/')}/${newName}` : newName;
    
    // Check if file already exists
    if (state.files.some(f => f.filename === newFilename && f.id !== id)) {
      alert('A file with this name already exists');
      return;
    }
    
    dispatch({ type: 'RENAME_FILE', id, newFilename });
  };

  const renameFolder = (folderPath) => {
    const currentName = folderPath.split('/').pop();
    const newName = prompt('Enter new folder name:', currentName);
    if (!newName || newName === currentName) return;
    
    const pathParts = folderPath.split('/');
    pathParts.pop(); // Remove the folder name
    const newPath = pathParts.length > 0 ? `${pathParts.join('/')}/${newName}` : newName;
    
    // Check if folder already exists
    if (state.folders.some(f => f === newPath)) {
      alert('A folder with this name already exists');
      return;
    }
    
    dispatch({ type: 'RENAME_FOLDER', oldPath: folderPath, newPath });
  };



  const exportAsFiles = async () => {
    try {
      // Try to use JSZip if available
      try {
        const JSZip = (await import('jszip')).default;
        const zip = new JSZip();
        
        // Add all files to the zip
        state.files.forEach(file => {
          zip.file(file.filename, file.content || '');
        });
        
        // Generate the zip file
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        
        // Create download link
        const url = URL.createObjectURL(zipBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `lld-project-${new Date().toISOString().split('T')[0]}.zip`;
        
        // Trigger download
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
      } catch (zipError) {
        // Fallback: Download files individually
        downloadFilesIndividually();
      }
      
    } catch (error) {
      console.error('Error creating zip file:', error);
      alert('Error creating zip file. Please try again.');
    }
  };

  const downloadFilesIndividually = () => {
    // Create a folder structure and download files one by one
    const projectName = `lld-project-${new Date().toISOString().split('T')[0]}`;
    
    state.files.forEach((file, index) => {
      setTimeout(() => {
        const blob = new Blob([file.content || ''], { 
          type: 'text/plain' 
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = file.filename;
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, index * 100); // Small delay between downloads
    });
    
    // Also create a README file with project info
    setTimeout(() => {
      const readmeContent = `# LLD Project Export

This project was exported from the LLD Editor.

## Files:
${state.files.map(file => `- ${file.filename}`).join('\n')}

## Folders:
${state.folders.map(folder => `- ${folder}/`).join('\n')}

## Instructions:
1. Create the folder structure as shown above
2. Place each file in its corresponding location
3. Open the project in your preferred IDE

Exported on: ${new Date().toLocaleString()}
`;

      const blob = new Blob([readmeContent], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'README.md';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, state.files.length * 100 + 100);
  };



  const importFilesAndFolders = () => {
    setShowImportModal(true);
  };



  const handleImportFiles = () => {
    setShowImportModal(false);
    // Import files
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = '.java,.js,.ts,.jsx,.tsx,.py,.cpp,.c,.h,.hpp,.cs,.php,.rb,.go,.rs,.swift,.kt,.scala,.clj,.hs,.ml,.fs,.vbs,.ps1,.sh,.bat,.sql,.html,.css,.xml,.json,.txt,.md';
    input.style.display = 'none';
    
    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;
      
      // Import individual files
      files.forEach((file, index) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const content = event.target.result;
            const filename = file.name;
            
            // Determine the target path based on current folder
            const targetPath = state.selectedFolder ? `${state.selectedFolder}/${filename}` : filename;
            
            // Check if file already exists
            if (state.files.some(f => f.filename === targetPath)) {
              const newName = prompt(`File "${filename}" already exists. Enter new name:`, filename);
              if (!newName) return;
              const newPath = state.selectedFolder ? `${state.selectedFolder}/${newName}` : newName;
              dispatch({ type: 'ADD_FILE', filename: newPath, content });
            } else {
              dispatch({ type: 'ADD_FILE', filename: targetPath, content });
            }
            
          } catch (error) {
            console.error('Error reading file:', file.name, error);
          }
        };
        
        reader.readAsText(file);
      });
    };
    
    // Trigger file selection
    document.body.appendChild(input);
    input.click();
    document.body.removeChild(input);
  };

  const handleImportFolder = () => {
    setShowImportModal(false);
    // Import folders
    const input = document.createElement('input');
    input.type = 'file';
    input.webkitdirectory = true;
    input.multiple = true;
    input.style.display = 'none';
    
    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;
      
      // Import folders with structure
      const fileGroups = {};
      files.forEach(file => {
        const relativePath = file.webkitRelativePath || file.name;
        fileGroups[relativePath] = file;
      });
      
      // Extract and create folder structure first
      const foldersToCreate = new Set();
      Object.keys(fileGroups).forEach(relativePath => {
        const pathParts = relativePath.split('/');
        // Create all parent folders for each file
        for (let i = 1; i < pathParts.length; i++) {
          const folderPath = pathParts.slice(0, i).join('/');
          if (state.selectedFolder) {
            foldersToCreate.add(`${state.selectedFolder}/${folderPath}`);
          } else {
            foldersToCreate.add(folderPath);
          }
        }
      });
      
      // Create all folders first
      foldersToCreate.forEach(folderPath => {
        if (!state.folders.includes(folderPath)) {
          dispatch({ type: 'ADD_FOLDER', folder: folderPath });
        }
      });
      
      // Process each file
      Object.entries(fileGroups).forEach(([relativePath, file]) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const content = event.target.result;
            
            // Determine the target path
            let targetPath = relativePath;
            if (state.selectedFolder) {
              targetPath = `${state.selectedFolder}/${relativePath}`;
            }
            
            // Check if file already exists
            if (state.files.some(f => f.filename === targetPath)) {
              const newName = prompt(`File "${relativePath}" already exists. Enter new name:`, relativePath);
              if (!newName) return;
              const newPath = state.selectedFolder ? `${state.selectedFolder}/${newName}` : newName;
              dispatch({ type: 'ADD_FILE', filename: newPath, content });
            } else {
              dispatch({ type: 'ADD_FILE', filename: targetPath, content });
            }
            
          } catch (error) {
            console.error('Error reading file:', relativePath, error);
          }
        };
        
        reader.readAsText(file);
      });
    };
    
    // Trigger folder selection
    document.body.appendChild(input);
    input.click();
    document.body.removeChild(input);
  };

  const handleDragStart = (e, item) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify(item));
  };

  const handleDragOver = (e, targetItem) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverItem(targetItem);
  };

  const handleDragLeave = (e) => {
    setDragOverItem(null);
  };

  const handleDrop = (e, targetItem) => {
    e.preventDefault();
    if (!draggedItem || !targetItem) return;

    // Don't allow dropping on itself
    if (draggedItem.path === targetItem.path) return;

    // Don't allow dropping a folder into its own subfolder
    if (draggedItem.type === 'folder' && targetItem.path.startsWith(draggedItem.path + '/')) {
      return;
    }

    if (draggedItem.type === 'file') {
      let newFilename;
      
      if (targetItem.type === 'folder') {
        // Dropping on a folder - move file into that folder
        newFilename = `${targetItem.path}/${draggedItem.path.split('/').pop()}`;
      } else {
        // Dropping on a file - move to the same level as the target file
        const targetPathParts = targetItem.path.split('/');
        targetPathParts.pop(); // Remove the filename to get the parent directory
        const parentPath = targetPathParts.join('/');
        newFilename = parentPath ? `${parentPath}/${draggedItem.path.split('/').pop()}` : draggedItem.path.split('/').pop();
      }
      
      // Check if file already exists at destination
      if (state.files.some(f => f.filename === newFilename && f.id !== draggedItem.id)) {
        alert('A file with this name already exists at the destination');
        return;
      }

      dispatch({ type: 'MOVE_FILE', id: draggedItem.id, newFilename });
    } else if (draggedItem.type === 'folder') {
      let newPath;
      
      if (targetItem.type === 'folder') {
        // Dropping on a folder - move folder into that folder
        newPath = `${targetItem.path}/${draggedItem.path.split('/').pop()}`;
      } else {
        // Dropping on a file - move to the same level as the target file
        const targetPathParts = targetItem.path.split('/');
        targetPathParts.pop(); // Remove the filename to get the parent directory
        const parentPath = targetPathParts.join('/');
        newPath = parentPath ? `${parentPath}/${draggedItem.path.split('/').pop()}` : draggedItem.path.split('/').pop();
      }

      // Check if folder already exists at destination
      if (state.folders.some(f => f === newPath)) {
        alert('A folder with this name already exists at the destination');
        return;
      }

      dispatch({ type: 'MOVE_FOLDER', oldPath: draggedItem.path, newPath });
    }

    setDraggedItem(null);
    setDragOverItem(null);
  };

  // Show loading state until files are fetched
  if (state.isLoading) {
    return (
      <div className="explorer">
        <div className="explorer-loading">
          <div className="loading-spinner"></div>
          <p>Loading files...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* AI Feedback Button */}
      <div style={{ 
        padding: '10px 10px 0',
        flexShrink: 0
      }}>
      </div>

      <div className="explorer">
        <div className="explorer-header">
          <button onClick={addFile} title="New File">＋</button>{' '}
          <button onClick={addFolder} title="New Folder">📁</button>{' '}
          <button onClick={runFiles} disabled={state.loading} title="Run" style={{marginLeft:4}}>
            {state.loading ? '⏳' : '▶'}
          </button>
          <button onClick={exportAsFiles} title="Export Project" style={{marginLeft:4}}>
            ⬆️
          </button>
          <button onClick={importFilesAndFolders} title="Import Files/Folders" style={{marginLeft:4}}>
            ⬇️
          </button>
        </div>

        {/* Custom Import Modal */}
        {showImportModal && (
          <div className="import-modal-overlay" onClick={() => setShowImportModal(false)}>
            <div className="import-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Choose Import Type</h3>
              <div className="import-buttons">
                <button onClick={handleImportFiles} className="import-btn files-btn">
                  Files
                </button>
                <button onClick={handleImportFolder} className="import-btn folder-btn">
                  Folder
                </button>
                <button onClick={() => setShowImportModal(false)} className="import-btn cancel-btn">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        
        <ul className="file-tree">
          {[...state.folders.map((f)=>({type:'folder',path:f})),
            ...state.files.map((f)=>({type:'file',path:f.filename,id:f.id}))]
            .sort((a,b)=> a.path.localeCompare(b.path))
            .map(item=>{
              const depth=item.path.split('/').length-1;
              const padding=depth*INDENT+4;
              const isHidden = state.collapsed.some(col=> item.path.startsWith(col+'/') && col!==item.path);
              if(isHidden) return null;

              if(item.type==='folder'){
                const collapsed = state.collapsed.includes(item.path);
                const isDragging = draggedItem?.path === item.path;
                const isDragOver = dragOverItem?.path === item.path;
                return (
                  <li key={item.path}
                      style={{paddingLeft:padding}}
                      className={`${item.path===state.selectedFolder ? 'active' : ''} ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''}`}
                      draggable={true}
                      onDragStart={(e) => handleDragStart(e, item)}
                      onDragOver={(e) => handleDragOver(e, item)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, item)}
                      onClick={()=>dispatch({type:'SET_SELECTED_FOLDER',folder:item.path})}
                  >
                    <span onClick={(e)=>{e.stopPropagation();dispatch({type:'TOGGLE_COLLAPSE',folder:item.path});}} style={{cursor:'pointer',marginRight:4}}><Arrow collapsed={collapsed}/></span>
                    📁 {item.path.split('/').pop()}/
                    <span className="item-rename" onClick={(e)=>{e.stopPropagation();renameFolder(item.path);}} title="Rename folder">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                    </span>
                    <span className="item-delete" onClick={(e)=>{e.stopPropagation();deleteFolder(item.path);}} title="Delete folder">🗑</span>
                  </li>
                );
              }
              return (
                <li key={item.id}
                    style={{paddingLeft:padding}}
                    className={`${item.id===state.activeFileId && !state.selectedFolder ? 'active' : ''} ${draggedItem?.id === item.id ? 'dragging' : ''} ${dragOverItem?.id === item.id ? 'drag-over' : ''}`}
                    draggable={true}
                    onDragStart={(e) => handleDragStart(e, item)}
                    onDragOver={(e) => handleDragOver(e, item)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, item)}
                    onClick={()=>dispatch({type:'SET_ACTIVE',id:item.id})}
                >
                  📄 {item.path.split('/').pop()}
                  <span className="item-rename" onClick={(e)=>{e.stopPropagation();renameFile(item.id, item.path);}} title="Rename file">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                      <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                  </span>
                  <span className="item-delete" onClick={(e)=>{e.stopPropagation();deleteFile(item.id);}} title="Delete file">🗑</span>
                </li>
              );
            })}
        </ul>
      </div>
    </div>
  );
} 